import { useState, useEffect, useRef, useCallback } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import isEqual from 'lodash/isEqual';
import { useNavigate } from 'react-router-dom';
import useApiFetch from '@/hooks/useApiFetch';
import { getEndpointWithParams } from '@/routes/config';
import jobsService, { JobResponse } from '@/services/jobs';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds } from '@/types';
import { Paths } from '@/types/routes';
import JobsListComponent from './JobsList.component';
import { DEFAULT_PAGINATION_DATA, DEFAULT_SORT_BY } from './JobsList.constants';
import {
  getPaginationDataFromLocalStorage,
  PaginationData,
  setPaginationDataToLocalStorage,
} from './JobsList.utils';

const JobsListPage = function (): JSX.Element {
  const navigate = useNavigate();
  const [isNewJobFormOpened, setIsNewJobFormOpened] = useState(false);
  const isAddingNewJob = useRef(false);

  const [pagination, setPagination] = useState<PaginationData>(DEFAULT_PAGINATION_DATA);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);

  const onUpdateSortBy = (event): void => {
    const {
      target: { value, name },
    } = event;
    setSortBy((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const paginationData = getPaginationDataFromLocalStorage();

    if (paginationData) {
      setPagination(paginationData);
    }
  }, []);

  useEffect(() => {
    const isDefaultSortBy = isEqual(sortBy, DEFAULT_SORT_BY);

    if (!isDefaultSortBy) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  }, [sortBy]);

  useEffect(() => {
    const isDefaultData = isEqual(pagination, DEFAULT_PAGINATION_DATA);

    if (!isDefaultData) {
      setPaginationDataToLocalStorage(pagination);
    }
  }, [pagination]);

  const jobsResponse = useApiFetch<JobResponse>(
    [
      FetchApiIds.JOBS_LIST,
      sortBy.order,
      sortBy.column,
      pagination.currentPage,
      pagination.itemsPerPage,
    ],
    async () =>
      jobsService.getAllJobsSorted({
        take: pagination.itemsPerPage,
        page: pagination.currentPage,
        column: sortBy.column,
        order: sortBy.order,
      }),
    {
      retry: 1,
    }
  );

  useEffect(() => {
    if (jobsResponse.data?.currentPage) {
      setPagination((prev) => ({
        ...prev,
        count: jobsResponse.data?.count,
        currentPage: jobsResponse.data?.currentPage,
        lastPage: jobsResponse.data.lastPage,
      }));
    }
  }, [jobsResponse.data?.count, jobsResponse.data?.currentPage, jobsResponse.data?.lastPage]);

  const onChangePageHandler = (currentPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage,
    }));
  };

  const onChangeRecordsPerPage = (itemsPerPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage,
    }));
  };

  const onShowCandidateFormClick = (event): void => {
    event.stopPropagation();
    setIsNewJobFormOpened(true);
  };

  const onHideCandidateFormClick = (): void => {
    setIsNewJobFormOpened(false);
  };

  const onDirtyForm = useCallback((isDirty: boolean): void => {
    isAddingNewJob.current = isDirty;
  }, []);

  const onPageClick = useCallback(
    (event): void => {
      if (isAddingNewJob.current || !isNewJobFormOpened) {
        return;
      }

      const eventPath = event.composedPath() ?? [];

      const formId = 'new-job-form';
      const doesElementContainId = (element): boolean => {
        return element?.id?.includes(formId);
      };

      const isClickingInsideForm = eventPath.some(doesElementContainId);

      if (!isClickingInsideForm) {
        onHideCandidateFormClick();
      }
    },
    [isNewJobFormOpened]
  );

  const onAddNewJob = useCallback(
    (id: string) => {
      jobsResponse.refetch();
      navigate(
        getEndpointWithParams(Paths.JOB_DETAILS, {
          jobId: id,
        })
      );
    },
    [jobsResponse, navigate]
  );

  useEffect(() => {
    window.addEventListener('click', onPageClick);
    return () => window.addEventListener('click', onPageClick);
  }, [onPageClick]);

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <JobsListComponent
        {...pagination}
        {...sortBy}
        onAddNewJob={onAddNewJob}
        onDirtyForm={onDirtyForm}
        isNewJobFormOpened={isNewJobFormOpened}
        onShowCandidateFormClick={onShowCandidateFormClick}
        onHideJobFormClick={onHideCandidateFormClick}
        onUpdateSortBy={onUpdateSortBy}
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        isLoading={jobsResponse.isLoading}
        onChangePageHandler={onChangePageHandler}
        jobsList={jobsResponse?.data?.data ?? []}
      />
    </ChakraProvider>
  );
};

export default JobsListPage;
